import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { toast } from 'sonner';
import { 
  CreditCard, 
  CheckCircle, 
  AlertTriangle, 
  Clock, 
  ExternalLink,
  Loader2,
  Calendar,
  Building2,
  Download,
  AlertCircle
} from 'lucide-react';

const BillingPage = () => {
  const { api, fetchBillingStatus: refreshAuthBilling } = useAuth();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [billingStatus, setBillingStatus] = useState(null);
  const [invoices, setInvoices] = useState([]);

  const fetchBillingStatus = useCallback(async () => {
    try {
      const [statusRes, invoicesRes] = await Promise.all([
        api.get('/billing/subscription/status'),
        api.get('/billing/invoices').catch(() => ({ data: [] }))
      ]);
      setBillingStatus(statusRes.data);
      setInvoices(invoicesRes.data || []);
    } catch (error) {
      if (error.response?.status !== 402) {
        toast.error('Failed to load billing information');
      }
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchBillingStatus();
    
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      toast.success('Subscription setup successful! Your 14-day free trial has started.');
      window.history.replaceState({}, '', '/billing');
      // Refresh auth context billing status
      refreshAuthBilling();
    }
  }, [searchParams, fetchBillingStatus, refreshAuthBilling]);

  const handleManageBilling = async () => {
    setActionLoading(true);
    try {
      const response = await api.post('/billing/subscription/billing-portal', {
        origin_url: window.location.origin
      });
      if (response.data.portal_url) {
        window.location.href = response.data.portal_url;
      }
    } catch (error) {
      const detail = error.response?.data?.detail || 'Failed to open billing portal';
      toast.error(detail);
    } finally {
      setActionLoading(false);
    }
  };

  const handleStartSubscription = async () => {
    setActionLoading(true);
    try {
      const response = await api.post('/billing/subscription/checkout-session', {
        origin_url: window.location.origin
      });
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
    } catch (error) {
      if (error.response?.status === 409) {
        toast.info('You already have an active subscription. Opening billing portal...');
        handleManageBilling();
      } else {
        const detail = error.response?.data?.detail || 'Failed to start subscription';
        toast.error(detail);
      }
    } finally {
      setActionLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      trialing: { color: 'bg-blue-100 text-blue-700', icon: Clock, label: 'Trial' },
      active: { color: 'bg-green-100 text-green-700', icon: CheckCircle, label: 'Active' },
      past_due: { color: 'bg-red-100 text-red-700', icon: AlertTriangle, label: 'Past Due' },
      unpaid: { color: 'bg-red-100 text-red-700', icon: AlertTriangle, label: 'Unpaid' },
      canceled: { color: 'bg-gray-100 text-gray-700', icon: AlertTriangle, label: 'Canceled' },
      none: { color: 'bg-gray-100 text-gray-700', icon: CreditCard, label: 'No Subscription' }
    };
    const config = statusConfig[status] || statusConfig.none;
    const Icon = config.icon;
    return (
      <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium ${config.color}`} data-testid="subscription-status-badge">
        <Icon className="w-4 h-4" />
        {config.label}
      </span>
    );
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric', month: 'long', day: 'numeric'
    });
  };

  const hasSubscription = billingStatus?.subscription_status && billingStatus.subscription_status !== 'none';
  const facilityCount = billingStatus?.facility_count || 0;
  const pricePerFacility = billingStatus?.price_per_facility || 199.99;

  const getInvoiceStatusStyle = (status) => {
    switch (status) {
      case 'PAID': return 'bg-green-50 text-green-700 border-green-200';
      case 'PENDING': return 'bg-yellow-50 text-yellow-700 border-yellow-200';
      case 'FAILED': return 'bg-red-50 text-red-700 border-red-200';
      default: return '';
    }
  };

  const getInvoiceStatusIcon = (status) => {
    switch (status) {
      case 'PAID': return <CheckCircle className="w-3.5 h-3.5" />;
      case 'PENDING': return <Clock className="w-3.5 h-3.5" />;
      case 'FAILED': return <AlertCircle className="w-3.5 h-3.5" />;
      default: return null;
    }
  };

  const formatCurrency = (amount) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-[#005EF8]" />
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-[#323033]">Billing & Subscription</h1>
        <p className="text-[#5A5A5A] mt-1">Manage your organization's subscription and payment methods</p>
      </div>

      {/* Warning Banner for Locked Accounts */}
      {billingStatus?.is_locked_for_billing && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3" data-testid="billing-locked-banner">
          <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="font-medium text-red-800">Account Access Restricted</h3>
            <p className="text-sm text-red-700 mt-1">
              Your subscription requires attention. Please update your billing information to restore full access.
            </p>
          </div>
        </div>
      )}

      {/* Subscription Status Card */}
      <Card className="mb-6">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-[#005EF8]" />
              Subscription Status
            </CardTitle>
            {billingStatus && getStatusBadge(billingStatus.subscription_status)}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[#005EF8]/10 flex items-center justify-center">
                <Building2 className="w-5 h-5 text-[#005EF8]" />
              </div>
              <div>
                <p className="text-sm text-[#5A5A5A]">Organization</p>
                <p className="font-medium">{billingStatus?.organization_name || '-'}</p>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[#005EF8]/10 flex items-center justify-center">
                <Building2 className="w-5 h-5 text-[#005EF8]" />
              </div>
              <div>
                <p className="text-sm text-[#5A5A5A]">Active Facilities</p>
                <p className="font-medium" data-testid="facility-count">{facilityCount}</p>
              </div>
            </div>

            {billingStatus?.subscription_status === 'trialing' && billingStatus?.trial_end_at && (
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center">
                  <Calendar className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-[#5A5A5A]">Trial Ends</p>
                  <p className="font-medium">{formatDate(billingStatus.trial_end_at)}</p>
                </div>
              </div>
            )}
          </div>

          {/* Pricing breakdown */}
          {hasSubscription && (
            <div className="mt-4 p-4 bg-[#FAFAFA] rounded-lg border">
              <div className="flex justify-between items-center">
                <div>
                  <span className="text-[#5A5A5A]">{facilityCount} {facilityCount === 1 ? 'facility' : 'facilities'} x ${pricePerFacility.toFixed(2)}/mo</span>
                </div>
                <span className="font-bold text-lg" data-testid="billing-total">
                  ${(facilityCount * pricePerFacility).toFixed(2)}<span className="text-sm font-normal text-[#5A5A5A]">/mo</span>
                </span>
              </div>
              {billingStatus?.subscription_status === 'trialing' && (
                <p className="text-xs text-blue-600 mt-2">First invoice will be created after your trial ends.</p>
              )}
            </div>
          )}

          {billingStatus?.subscription_status === 'trialing' && (
            <div className="p-3 bg-blue-50 border border-blue-100 rounded-lg">
              <p className="text-sm text-blue-800">
                <strong>You're on a free trial!</strong> Add a payment method before your trial ends to continue using Onsite Rail Solutions without interruption. Adding or removing facilities during the trial won't trigger any charges.
              </p>
            </div>
          )}

          {!hasSubscription && (
            <div className="p-3 bg-amber-50 border border-amber-100 rounded-lg">
              <p className="text-sm text-amber-800">
                <strong>No active subscription.</strong> Set up billing to start your 14-day free trial. You won't be charged until the trial ends.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Actions Card */}
      <Card>
        <CardHeader>
          <CardTitle>Manage Billing</CardTitle>
          <CardDescription>
            {hasSubscription 
              ? 'Update your payment method, view invoices, or manage your subscription'
              : 'Start your 14-day free trial — no credit card required upfront'}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {hasSubscription ? (
            <Button
              onClick={handleManageBilling}
              disabled={actionLoading}
              className="bg-[#005EF8] hover:bg-[#0047C2] text-white"
              data-testid="manage-billing-btn"
            >
              {actionLoading ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Opening...</>
              ) : (
                <><CreditCard className="w-4 h-4 mr-2" />Manage Billing<ExternalLink className="w-4 h-4 ml-2" /></>
              )}
            </Button>
          ) : (
            <Button
              onClick={handleStartSubscription}
              disabled={actionLoading}
              className="bg-[#005EF8] hover:bg-[#0047C2] text-white"
              data-testid="start-subscription-btn"
            >
              {actionLoading ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Setting up...</>
              ) : (
                <><CreditCard className="w-4 h-4 mr-2" />Start Free Trial<ExternalLink className="w-4 h-4 ml-2" /></>
              )}
            </Button>
          )}
          
          <p className="text-xs text-[#808080]">
            You'll be redirected to our secure payment portal powered by Stripe.
          </p>
        </CardContent>
      </Card>

      {/* Billing History */}
      <Card data-testid="invoices-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Download className="w-5 h-5 text-[#005EF8]" />
            Billing History
          </CardTitle>
        </CardHeader>
        <CardContent>
          {invoices.length === 0 ? (
            <div className="text-center py-8">
              <CreditCard className="w-12 h-12 text-[#D3D5D4] mx-auto mb-4" />
              <p className="text-[#5A5A5A]">No invoices yet</p>
              {hasSubscription && billingStatus?.subscription_status === 'trialing' && (
                <p className="text-xs text-[#808080] mt-1">Your first invoice will be generated after the trial ends.</p>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Invoice #</th>
                    <th>Description</th>
                    <th>Amount</th>
                    <th>Date</th>
                    <th>Status</th>
                    <th className="text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.map((invoice) => (
                    <tr key={invoice.id} data-testid={`invoice-row-${invoice.id}`}>
                      <td className="font-medium text-[#005EF8]">{invoice.invoice_number}</td>
                      <td>{invoice.description}</td>
                      <td>{formatCurrency(invoice.amount)}</td>
                      <td>{formatDate(invoice.created_at)}</td>
                      <td>
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium border ${getInvoiceStatusStyle(invoice.status)}`}>
                          {getInvoiceStatusIcon(invoice.status)}
                          {invoice.status}
                        </span>
                      </td>
                      <td className="text-right">
                        <Button variant="ghost" size="sm" data-testid={`download-invoice-${invoice.id}`}>
                          <Download className="w-4 h-4 mr-1" />
                          PDF
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default BillingPage;
